import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  CircularProgress,
  FilterOptionsState,
  SxProps,
  TextField,
  Checkbox,
} from "@mui/material";
import { useAsyncSelect } from "data/hooks/useAsyncSelect";
import AppIcon from "ui/components/icons/AppIcon";
import {
  iconCheckBoxOutlineBlank,
  iconCheckBox,
  iconClose,
  iconExpandMore,
} from "ui/components/icons";

import React, { useCallback, useState } from "react";

import { useAdvancedViewFiltersStore } from "data/store/global-search/AdvancedViewFiltersStore";
import { GlobalSearchSelectOption } from "data/@types/GlobalSearchData";

type MultiSelectFilterProps<T> = {
  id: string;
  value?: T | string | null;
  label: string;
  fetchOptions?: (props?: string, page?: number) => Promise<T[]>;
  getOptionLabel?: (option: T | string) => string;
  getOptionKey?: (option: T | string) => string | number;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  shouldValidate?: boolean;
  editMode: boolean;
  cacheKey?: string;
  sx?: SxProps;
  useSearchAndPagination?: boolean;
  disabled?: boolean;
  required?: boolean;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  simpleSelect?: boolean;
  simpleOptions?: string[];
  activityType: GlobalSearchSelectOption;
};

const ClearIcon = () => <AppIcon icon={iconClose} fontSize="small" />;
const PopupIcon = () => <AppIcon icon={iconExpandMore} fontSize="small" />;

const defaultGetOptionLabel = <T,>(option: T): string => {
  if (typeof option === "string") return option;
  return option ? option.toString() : "";
};

const defaultGetOptionKey = <T,>(option: T): string | number => {
  if (typeof option === "object" && option !== null) {
    return (option as any).id?.toString() || JSON.stringify(option);
  }

  if (typeof option === "number" || typeof option === "string") {
    return option;
  }

  return String(option);
};

const defaultIsOptionEqualToValue = <T,>(option: T, value: T): boolean => {
  if (value === "") return true;
  return option === value;
};

export const MultiSelectFilter = <T,>({
  id,
  disabled,
  label,
  fetchOptions,
  getOptionKey = defaultGetOptionKey,
  getOptionLabel = defaultGetOptionLabel,
  isOptionEqualToValue = defaultIsOptionEqualToValue,
  filterOptions,
  useSearchAndPagination,
  simpleSelect,
  simpleOptions = [],
  activityType,
}: MultiSelectFilterProps<T>) => {
  const {
    open,
    setOpen,
    options,
    loading,
    inputText,
    setInputText,
    fetchMoreOptions,
    resetSearch,
    loadOptions,
    debouncedLoadOptions,
  } = useAsyncSelect(
    id,
    fetchOptions ?? (() => Promise.resolve([])),
    !simpleSelect,
    getOptionKey,
  );

  const activeFilters = useAdvancedViewFiltersStore(
    (state) => state.filtersByActivity[activityType] || {},
  );
  const setActiveFilters = useAdvancedViewFiltersStore(
    (state) => state.setFiltersForActivity,
  );

  const [value, setValue] = useState<string[]>(() => {
    const filterValue = activeFilters[label]?.valueText;
    if (Array.isArray(filterValue)) {
      return filterValue;
    }
    if (typeof filterValue === "string") {
      return [filterValue];
    }
    return [];
  });

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen, useSearchAndPagination, loadOptions]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen, useSearchAndPagination, resetSearch]);

  const handleInputChange = useCallback(
    (
      _event: React.SyntheticEvent,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      setInputText(newValue);
      if (useSearchAndPagination && newValue.length > 2 && reason === "input") {
        debouncedLoadOptions(newValue);
      }
    },
    [useSearchAndPagination, debouncedLoadOptions, setInputText],
  );

  React.useEffect(() => {
    setActiveFilters(activityType, {
      ...activeFilters,
      [label]: {
        ...activeFilters[label],
        valueText: value,
        operator: "contains",
      },
    });
  }, [value]);

  return (
    <Box>
      <Autocomplete
        id={id}
        clearIcon={<ClearIcon />}
        popupIcon={<PopupIcon />}
        value={value}
        freeSolo={useSearchAndPagination}
        multiple
        size="small"
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        disableCloseOnSelect
        onClose={handleClose}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        options={simpleSelect ? simpleOptions : options}
        filterOptions={filterOptions}
        loading={loading}
        inputValue={inputText}
        onInputChange={handleInputChange}
        onChange={(_, newValue) => setValue(newValue.map(getOptionLabel))}
        sx={{
          ".MuiOutlinedInput-notchedOutline, .Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderLeftWidth: "4px !important",
            },
          ".MuiFormHelperText-root": {
            marginLeft: "0",
          },
        }}
        renderOption={({ key, ...optionProps }, option, { selected }) => {
          return (
            <li key={key} {...optionProps}>
              <Checkbox
                icon={
                  <AppIcon icon={iconCheckBoxOutlineBlank} fontSize="small" />
                }
                checkedIcon={<AppIcon icon={iconCheckBox} fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option as string}
            </li>
          );
        }}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight ===
                listboxNode.scrollHeight &&
              useSearchAndPagination
            ) {
              fetchMoreOptions();
            }
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};
